<header class="flex header">
  <h2>Balanceo de Production Cards</h2>
  @if (canViewProportion) {
    <p-selectbutton [ngModel]="ratioMode()" (ngModelChange)="ratioMode.set($event)" [options]="ratioModeOptions" optionLabel="label" optionValue="value" [allowEmpty]="false" />
  }
  <p-button icon="pi pi-refresh" [loading]="isLoading() > 0" (onClick)="load()" pTooltip="Reload" />
</header>

<section class="content">
  @if (isLoading() > 0) {
    <loading-overlay [isLoading]="true"></loading-overlay>
  }

  @if (summary; as s) {
    @for (color of colors(); track color.colorName) {
      <!-- 1. La info: qué se planeó, qué salió y cuánto sobra o falta contra el ratio -->
      <p-panel [header]="s.productionCardNumber">
        <p-fieldset>
          <ng-template #header>
            <div class="flex items-center gap-[0.5rem]">
              <span class="color-swatch" [style.background]="color.colorHex"></span>
              <span class="color-name">{{ color.colorName }}</span>
            </div>
          </ng-template>

          <div class="plan-summary">
            <span>
              <span class="plan-label">Meta original de la PC:</span>
              @for (row of color.fabrics; track row.fabricCode) {
                <b>{{ row.plan | number: '1.0-0' }}</b> {{ row.fabricType }}{{ $last ? '' : ' · ' }}
              }
              ({{ planTotal(color) | number: '1.0-0' }} {{ s.unit }})
            </span>
            <span></span>
            <span>
              <span class="plan-label">{{ ratioMode() === 'proportion' ? 'Ratio objetivo (÷ total)' : 'Ratio objetivo (÷ ancla)' }}:</span>
              @for (row of color.fabrics; track row.fabricCode) {
                <b>{{ targetRatioDisplay(color, row) | number: '1.2-2' }}%</b>{{ $last ? '' : ' / ' }}
              }
            </span>
          </div>

          <div class="fabric-columns">
            @for (row of color.fabrics; track row.fabricCode) {
              <div class="fabric-column">
                <div class="fabric-type">{{ row.fabricType }}</div>
                <div class="fabric-code">
                  @if (row.isAnchor) {
                    <span class="anchor-mark">ANCLA</span>
                  }
                  ({{ row.fabricCode }})
                </div>

                <!-- Ratio actual vs ratio objetivo, not pounds. No client tolerance here: both
                     limits sit on the target ratio, so the dashed line marks exactly where the
                     excess over it begins. -->
                <div class="meter-wrapper">
                  <csl-tolerance-meter
                    orientation="vertical"
                    [segments]="segments(color, row)"
                    [scaleMin]="0"
                    [scaleMax]="scaleMax(color, row)"
                    [toleranceMin]="targetRatioDisplay(color, row)"
                    [toleranceMax]="targetRatioDisplay(color, row)"
                    [target]="targetRatioDisplay(color, row)"
                  />
                  <span class="target-label" [style.bottom.%]="targetLabelPosition(color, row)"> {{ targetRatioDisplay(color, row) | number: '1.2-2' }}% </span>
                </div>

                <p-tag [value]="'Actual: ' + (realRatio(color, row) | number: '1.2-2') + '%'" severity="secondary" />

                <div class="fabric-quantity" [class.is-off]="!row.isAnchor && difference(color, row) !== 0">Real: {{ row.real | number: '1.0-0' }} {{ s.unit }}</div>
                <div class="fabric-quantity">
                  @if (row.isAnchor) {
                    Plan: {{ row.plan | number: '1.0-0' }} {{ s.unit }}
                  } @else {
                    Objetivo recalculado: {{ target(color, row) | number: '1.0-0' }} {{ s.unit }}
                  }
                </div>

                <p-tag [value]="actionLabel(color, row)" [severity]="actionSeverity(color, row)" />
              </div>
            }
          </div>
        </p-fieldset>
      </p-panel>

      <!-- 2. Opción A: recortar el excedente -->
      <p-panel header="Opción A — Split y marcar como sobrante">
        <p class="option-description">
          Recorte el excedente de Rib y Tubular mediante split; el remanente queda como Left Over Assignable, disponible para otras Production Cards del mismo Item + Color.
        </p>

        @if (splitCandidates(color).length) {
          <ul class="split-hints">
            @for (candidate of splitCandidates(color); track candidate.fabricType) {
              <li>
                <a href="javascript:void(0)" (click)="onSplit(candidate)">{{ splitHintText(candidate) }}</a>
              </li>
            }
          </ul>
        } @else {
          <p class="option-empty">Ningún fabric pasa del ratio objetivo, no hay nada que recortar.</p>
        }
      </p-panel>

      <!-- 3. Opción B: cubrir el faltante con sobrantes de otras cards -->
      <p-panel>
        <ng-template #header>
          <h4>Opción B — Sobrantes disponibles: {{ s.leftOverFabricCode }} · {{ s.leftOverColorName }} (en Finishing / Rolling)</h4>
        </ng-template>

        <p-table [value]="s.availableRolls" styleClass="p-datatable-sm">
          <ng-template pTemplate="header">
            <tr>
              <th>Roll Number</th>
              <th>Peso</th>
              <th>Ubicación</th>
              <th>Origen</th>
              <th></th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-roll>
            <tr>
              <td>{{ roll.rollNumber }}</td>
              <td>{{ roll.weightLbs | number: '1.0-0' }} {{ s.unit }}</td>
              <td>{{ roll.location }}</td>
              <td class="roll-origin">Sobrante de {{ roll.originProductionCardNumber }}</td>
              <td class="text-right">
                <p-button label="Reasignar" (onClick)="onReassign(roll)" />
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="5">No hay sobrantes disponibles de este Item + Color en Finishing ni Rolling.</td>
            </tr>
          </ng-template>
        </p-table>
      </p-panel>
    }
  }
</section>

<footer>
  <p-button class="w-full" label="Close" variant="outlined" (click)="onClose()" />
</footer>
